import React, { useState, useEffect } from 'react';
import {
  Form,
  Card,
  Row,
  Col,
  Select,
  Switch,
  Table,
  Input,
  Button,
  Tag,
  Typography,
  Alert,
  Divider,
  message,
  Avatar,
  Spin
} from 'antd';
import {
  UserOutlined,
  SearchOutlined,
  DeleteOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { gqlClient } from '../../../helpers/gqlProvider';
import { SUPPLIER_PORTAL_SUPPLIERS_QUERY } from '../../sourcing/queries';

const { Option } = Select;
const { Text, Title } = Typography;
const { Search } = Input;

interface Supplier {
  id: string;
  companyName: string;
  description?: string;
  contactInfo: {
    email: string;
    phone: string;
    address: string;
  };
  services: string[];
  industries: string[];
  verificationStatus: 'verified' | 'unverified' | 'in_progress';
  profileProgress: number;
}

interface InviteTabProps {
  form: any;
  formData: any;
  sourcingData: any;
}

export const InviteTab: React.FC<InviteTabProps> = ({
  form,
}) => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedSuppliers, setSelectedSuppliers] = useState<string[]>([]);

  useEffect(() => {
    const fetchSuppliers = async () => {
      setLoading(true);
      try {
        const response = await gqlClient.query(SUPPLIER_PORTAL_SUPPLIERS_QUERY, {});
        if (response.data?.supplierPortalSuppliers) {
          setSuppliers(response.data.supplierPortalSuppliers);
          setFilteredSuppliers(response.data.supplierPortalSuppliers);
        } else {
          console.error('No suppliers data received:', response);
          message.error('Failed to load suppliers. Please try again.');
        }
      } catch (error) {
        console.error('Error fetching suppliers:', error);
        message.error('Failed to load suppliers. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchSuppliers();
  }, []);

  useEffect(() => {
    const currentSelected = form.getFieldValue('invitedSupplierIds') || [];
    setSelectedSuppliers(currentSelected);
  }, [form]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    const filtered = suppliers.filter(supplier =>
      supplier.companyName.toLowerCase().includes(value.toLowerCase()) ||
      supplier.services.some(service => service.toLowerCase().includes(value.toLowerCase())) ||
      supplier.industries.some(industry => industry.toLowerCase().includes(value.toLowerCase()))
    );
    setFilteredSuppliers(filtered);
  };

  const handleSupplierSelect = (supplierId: string) => {
    const currentSelected = form.getFieldValue('invitedSupplierIds') || [];
    const newSelected = [...currentSelected, supplierId];
    form.setFieldsValue({ invitedSupplierIds: newSelected });
    setSelectedSuppliers(newSelected);
  };

  const handleSupplierDeselect = (supplierId: string) => {
    const currentSelected = form.getFieldValue('invitedSupplierIds') || [];
    const newSelected = currentSelected.filter((id: string) => id !== supplierId);
    form.setFieldsValue({ invitedSupplierIds: newSelected });
    setSelectedSuppliers(newSelected);
  };

  const handlePublicToggle = (checked: boolean) => {
    form.setFieldsValue({ isPublic: checked });
    if (checked) {
      // For public sourcings, clear selected suppliers
      form.setFieldsValue({ invitedSupplierIds: [] });
      setSelectedSuppliers([]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'green';
      case 'in_progress':
        return 'orange';
      case 'unverified':
        return 'red';
      default:
        return 'default';
    }
  };

  const supplierColumns = [
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (name: string, record: Supplier) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.contactInfo.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Services',
      dataIndex: 'services',
      key: 'services',
      render: (services: string[]) => (
        <div>
          {services.slice(0, 2).map((service, index) => (
            <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
              {service}
            </Tag>
          ))}
          {services.length > 2 && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              +{services.length - 2} more
            </Text>
          )}
        </div>
      ),
    },
    {
      title: 'Industries',
      dataIndex: 'industries',
      key: 'industries',
      render: (industries: string[]) => (
        <div>
          {industries.map((industry, index) => (
            <Tag key={index} color="purple" style={{ marginBottom: 4 }}>
              {industry}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'verificationStatus',
      key: 'verificationStatus',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {status.replace('_', ' ').toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Profile',
      dataIndex: 'profileProgress',
      key: 'profileProgress',
      render: (progress: number) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: '12px' }}>{progress}%</div>
          <div style={{ width: 60, height: 6, background: '#f0f0f0', borderRadius: 3 }}>
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                background: progress >= 80 ? '#52c41a' : progress >= 50 ? '#faad14' : '#ff4d4f',
                borderRadius: 3,
                transition: 'width 0.3s'
              }}
            />
          </div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Supplier) => (
        <Button
          type={selectedSuppliers.includes(record.id) ? 'default' : 'primary'}
          onClick={() =>
            selectedSuppliers.includes(record.id)
              ? handleSupplierDeselect(record.id)
              : handleSupplierSelect(record.id)
          }
        >
          {selectedSuppliers.includes(record.id) ? 'Deselect' : 'Select'}
        </Button>
      ),
    },
  ];

  const selectedSupplierColumns = [
    {
      title: 'Company',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (name: string, record: Supplier) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Avatar icon={<UserOutlined />} />
          <div>
            <div style={{ fontWeight: 'bold' }}>{name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.contactInfo.email}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Contact',
      key: 'contact',
      render: (_: any, record: Supplier) => (
        <div style={{ fontSize: '12px' }}>
          <div><PhoneOutlined /> {record.contactInfo.phone}</div>
          <div><MailOutlined /> {record.contactInfo.email}</div>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Supplier) => (
        <Button
          danger
          icon={<DeleteOutlined />}
          onClick={() => handleSupplierDeselect(record.id)}
        >
          Remove
        </Button>
      ),
    },
  ];

  const selectedSuppliersData = suppliers.filter(supplier =>
    selectedSuppliers.includes(supplier.id)
  );

  return (
    <div>
      <Alert
        message="Invite Suppliers"
        description="Choose how to invite suppliers to participate in your sourcing event."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Row gutter={24}>
        <Col span={24}>
          <Card title="Invitation Settings" style={{ marginBottom: 24 }}>
            <Form.Item
              name="invitedSupplierIds"
              style={{ display: 'none' }}
            >
              <Input />
            </Form.Item>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Invitation Mode"
                  name="invitationMode"
                  rules={[{ required: true, message: 'Please select invitation mode' }]}
                >
                  <Select labelRender={l=>l.value} placeholder="Choose invitation mode">
                    <Option value="CLOSED">
                      <div>
                        <Text strong>Closed Sourcing</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Invite specific suppliers only
                        </Text>
                      </div>
                    </Option>
                    <Option value="OPEN">
                      <div>
                        <Text strong>Open Sourcing</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          Public sourcing visible to all suppliers
                        </Text>
                      </div>
                    </Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Public Visibility"
                  name="isPublic"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren={<GlobalOutlined />}
                    unCheckedChildren={<UserOutlined />}
                    onChange={handlePublicToggle}
                  />
                </Form.Item>
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Make this sourcing visible to all registered suppliers
                </Text>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {form.getFieldValue('invitationMode') === 'CLOSED' && (
        <>
          <Card title="Available Suppliers" style={{ marginBottom: 24 }}>
            <div style={{ marginBottom: 16 }}>
              <Search
                placeholder="Search suppliers by name, services, or industries..."
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                style={{ width: 400 }}
                prefix={<SearchOutlined />}
              />
            </div>

            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>Loading suppliers...</div>
              </div>
            ) : (
              <Table
                dataSource={filteredSuppliers}
                columns={supplierColumns}
                rowKey="id"
                pagination={{
                  pageSize: 5,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) =>
                    `${range[0]}-${range[1]} of ${total} suppliers`,
                }}
                size="small"
                locale={{ emptyText: 'No suppliers found matching your search' }}
              />
            )}
          </Card>

          {selectedSuppliers.length > 0 && (
            <Card title="Selected Suppliers" style={{ marginBottom: 24 }}>
              <Table
                dataSource={selectedSuppliersData}
                columns={selectedSupplierColumns}
                rowKey="id"
                pagination={false}
                size="small"
              />

              <Divider />

              <div style={{ textAlign: 'right' }}>
                <Text type="secondary">
                  {selectedSuppliers.length} supplier{selectedSuppliers.length !== 1 ? 's' : ''} selected
                </Text>
              </div>
            </Card>
          )}
        </>
      )}

      {form.getFieldValue('invitationMode') === 'OPEN' && (
        <Card style={{ marginBottom: 24 }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <GlobalOutlined style={{ fontSize: '48px', color: '#1890ff', marginBottom: 16 }} />
            <Title level={4}>Public Sourcing</Title>
            <Text type="secondary">
              This sourcing will be visible to all registered suppliers on the platform.
              No specific invitations will be sent.
            </Text>
          </div>
        </Card>
      )}
    </div>
  );
};